// Prompt-var KV (#52, arc 101 slice 05) — a scoped key/value store whose values
// resolve `#{{key}}` placeholders in the org-layer + agent-template prompts at
// compose time, so ONE reusable template (`backend_dev`) carries per-repo values
// for `#{{stack}}`, `#{{test_cmd}}`, `#{{merge_lane}}`. Copies tesote.ai's
// prompt-cascade KV mechanics (`apps/backend/src/agent/prompt-cascade.ts`).
//
// Three scopes, general → specific: `account` (broadest default) < `template`
// (a shared "developer type") < `repo` (the concrete deployment target). On a
// key clash the MORE SPECIFIC scope wins — a per-repo value overrides a shared
// template value overrides an account default. `scopeRef` names the instance the
// value attaches to (a repo id / a template name); account scope has no instance,
// so its `scopeRef` is the empty string (NOT null — a text column keeps the
// `(account, scope, scope_ref, key)` uniqueness free of Postgres NULL-distinct
// footguns).
//
// The VALUE is arbitrary jsonb (scalar/array/object). It is user/operator content
// — the same injection posture as the layer-3 repo prompt — so the composer
// sanitizes every value (collapse line breaks) before it enters the prompt; a
// crafted multiline value can never smuggle a higher-priority instruction line.

import { z } from 'zod';
import { AccountIdSchema, PromptVarIdSchema } from '../value-objects/ids';

/**
 * The scope a prompt var attaches to. `account` is the broadest default;
 * `template` values specialize a shared agent template; `repo` values specialize
 * for one repository — the most specific, so it wins a key clash.
 */
export const PromptVarScope = {
  Account: 'account',
  Repo: 'repo',
  Template: 'template',
} as const;
export const PromptVarScopeSchema = z.enum(PromptVarScope);
export type PromptVarScope = z.infer<typeof PromptVarScopeSchema>;

/**
 * Specificity rank per scope — HIGHER wins on a key clash (general → specific:
 * account < template < repo). Drives both the substitution map (last write wins
 * when built in this order) and the compact config block's line order (specific
 * line reads later, so prompt-position precedence matches value precedence).
 */
export const PROMPT_VAR_SCOPE_PRECEDENCE = {
  [PromptVarScope.Account]: 0,
  [PromptVarScope.Template]: 1,
  [PromptVarScope.Repo]: 2,
} as const satisfies Record<PromptVarScope, number>;

/**
 * Whether a scope needs a `scopeRef` (the instance it attaches to). `repo` and
 * `template` do (a repo id / a template name); `account` does not (its ref is the
 * empty string). The MCP `config_set` verb enforces this before a write.
 */
export function promptVarScopeNeedsRef(scope: PromptVarScope): boolean {
  return scope !== PromptVarScope.Account;
}

/**
 * A prompt-var key — the left side of a `key: value` line and the body of a
 * `#{{key}}` placeholder. A superset-safe alphabet for the placeholder regex
 * (`[A-Za-z0-9_.-]`), trimmed, 1..128 chars. Dotted keys (`repo.stack`) are fine.
 */
export const ConfigKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(128)
  .regex(/^[A-Za-z0-9_.-]+$/, 'config key: letters, digits, dot, underscore, hyphen only');
export type ConfigKey = z.infer<typeof ConfigKeySchema>;

/**
 * THE `#{{key}}` placeholder — a leading `#`, `{{`, an optional-whitespace key
 * body of {@link ConfigKeySchema}'s alphabet, then `}}`. A bare `{{key}}` with no
 * leading `#` does NOT match, so a conventional template typo stays a visible
 * literal instead of silently corrupting the line.
 *
 * It lives HERE, beside the alphabet it is built from, because two packages read
 * it and neither may import the other: `@developerz/agent` SUBSTITUTES against it
 * at compose time (`prompt-vars.ts` `substitutePromptVars`, where a key with no
 * value renders a visible `⟨unset: key⟩`), and `@developerz/mcp` reads it at the
 * WRITE seam to tell an author which keys their prose depends on that nothing
 * resolves yet (`army/prompt.ts`). Two copies of this regex would mean a
 * placeholder spelling the composer honours and the write seam cannot see, which
 * is the exact silence that seam exists to break.
 *
 * NOT `g`-flagged: a shared `g` regex carries `lastIndex` across calls, so a
 * module-level one is a cross-call state bug waiting for its second reader. Each
 * consumer builds its own global copy from `.source`.
 */
export const PROMPT_VAR_PLACEHOLDER = /#\{\{\s*([A-Za-z0-9_.-]+)\s*\}\}/;

/**
 * Every distinct config key `text` interpolates, in the order they first appear
 * — what an author's prose DEPENDS ON, which is the question both the composer
 * and the write seam ask of one string. Empty for prose that interpolates
 * nothing. Pure.
 */
export function promptVarKeysIn(text: string): readonly string[] {
  const keys: string[] = [];
  for (const [, key] of text.matchAll(new RegExp(PROMPT_VAR_PLACEHOLDER.source, 'g'))) {
    if (key !== undefined && !keys.includes(key)) keys.push(key);
  }
  return keys;
}

/**
 * The stored value — arbitrary JSON (scalar/array/object/null). `undefined` is
 * not JSON, so it is rejected: a value is always present (use `null` for an
 * explicit empty). Bounded at the write boundary (`config_set`), not here, so a
 * read never rejects an already-stored large value.
 */
export const PromptVarValueSchema = z
  .unknown()
  .refine((v) => v !== undefined, 'prompt-var value is required (use null for an explicit empty)');

/**
 * One `prompt_vars` row. Parsed on the way OUT of the DB so a drifted write — bad
 * id prefix, unknown scope, malformed key — fails loudly at read, the same parse
 * gate `tasks` / `agent_templates` use. `scopeRef` is `''` for account scope.
 */
export const PromptVarSchema = z.object({
  /** `pvr_<hex>` — the branded id IS the stored text PK (same scheme as templates/tasks). */
  id: PromptVarIdSchema,
  accountId: AccountIdSchema,
  scope: PromptVarScopeSchema,
  /** The instance the value attaches to (a repo id / template name); `''` for account scope. */
  scopeRef: z.string().max(200),
  key: ConfigKeySchema,
  value: PromptVarValueSchema,
  createdAt: z.date(),
  updatedAt: z.date(),
});
export type PromptVar = z.infer<typeof PromptVarSchema>;

/**
 * The compose-facing slice of a prompt var: the scope tag + key + value the pure
 * renderer needs to build the `#{{key}}` substitution map and the compact
 * `[scope] key: value` block. `scopeRef` / ids / timestamps are irrelevant to
 * rendering, so they are dropped — the composer stays a pure data transform over
 * this minimal shape (mirrors tesote's `PromptConfigEntry`).
 */
export const PromptVarEntrySchema = z.object({
  scope: PromptVarScopeSchema,
  key: ConfigKeySchema,
  value: PromptVarValueSchema,
});
export type PromptVarEntry = z.infer<typeof PromptVarEntrySchema>;
